import { DeviceModelNumberKey } from '@csp/config-schemas';
import { MetricPeriod, StateAssert } from '@csp/csp-common-model';
import {
  GenericRequest,
  GenericRequestMapper,
  ReminderNotificationMetaMapper,
  RequestCriticalityConfigMapper,
  RequestGroupMapper,
  RequestInitiationConfigMapper,
  RequestTagsConfigMetaMapper,
  RpmEventConfigMapper,
  ScheduleCode,
  ScheduleValidationService,
  UnitOfTime,
  VersionCode,
} from '@csp/csp-common-scheduling';
import { DeviceModelNumberType } from '@csp/device-catalog';
import { DeviceObservationRequestDetailsV1, DeviceObservationRequestV1 } from '@csp/dmdp-api-device-dto';
import { DeviceMetricsConfigMapper } from '../mapper/DeviceMetricsConfigMapper';
import { DeviceObservationRequestDetails } from './DeviceObservationRequestDetails';

export type DeviceObservationRequestInfo = GenericRequest & {
  details: DeviceObservationRequestDetails;
  referencedModelNumbers: DeviceModelNumberType[];
  historicalMetricPeriod?: MetricPeriod;
};

const SUPPORTED_UNIT_OF_TIMES = [UnitOfTime.D, UnitOfTime.H, UnitOfTime.MIN];

const toReferencedModelNumbers = ({
  deviceModelReferences,
}: DeviceObservationRequestDetailsV1): DeviceModelNumberType[] =>
  deviceModelReferences
    .filter(({ ref }) => ref.key === DeviceModelNumberKey)
    .flatMap(({ ref }) => ref.value as DeviceModelNumberType);

const from = (
  scheduleCode: ScheduleCode,
  versionCode: VersionCode,
  { requestCode, startAction, endAction, timing, details, meta, while: requestWhile }: DeviceObservationRequestV1,
): DeviceObservationRequestInfo => {
  StateAssert.notNull(details, `DeviceObservationRequest details not set: ${requestCode}`);
  const referencedModelNumbers = toReferencedModelNumbers(details);
  const notificationConfig = ReminderNotificationMetaMapper.toNotificationConfigFromMetaV1(meta);
  const initiationConfig = RequestInitiationConfigMapper.fromRequestInitiationMetaV2(meta);
  const criticalityConfig = RequestCriticalityConfigMapper.fromRequestCriticalityMetaV1(meta);
  const metricConfig = DeviceMetricsConfigMapper.fromRequestMetricsConfigsMetaV1(meta);
  const rpmEventConfig = RpmEventConfigMapper.fromRpmEventConfigMetaV1(meta);
  const tagsConfig = RequestTagsConfigMetaMapper.fromRequestTagsConfigMetaV1(meta);
  const groups = RequestGroupMapper.toRequestGroupMemberships(meta);

  const minComplianceThreshold = metricConfig?.minComplianceThreshold;
  const historicalMetricPeriod = metricConfig?.historicalMetricPeriod;

  StateAssert.notEmpty(referencedModelNumbers, `DeviceObservationRequest device models not set: ${requestCode}`);
  const request: DeviceObservationRequestInfo = {
    requestCode,
    versionCode,
    scheduleCode,
    startAction: GenericRequestMapper.toRequestAction(startAction, SUPPORTED_UNIT_OF_TIMES),
    endAction: GenericRequestMapper.toRequestAction(endAction, SUPPORTED_UNIT_OF_TIMES),
    while: GenericRequestMapper.toRequestWhile(requestWhile),
    timing: GenericRequestMapper.toTiming(timing, {
      supportedUnitOfTimes: SUPPORTED_UNIT_OF_TIMES,
    }),
    details: DeviceObservationRequestDetails.from(details),
    referencedModelNumbers,
    notificationConfig,
    initiationConfig,
    minComplianceThreshold,
    historicalMetricPeriod,
    rpmEventConfig,
    criticalityConfig,
    tagsConfig,
    groups,
  };

  ScheduleValidationService.validateRequest(request);
  return request;
};

export const DeviceObservationRequestInfo = {
  from,
};
